/**
 * hkPriority — decides the cleaning order for Housekeeping Live (Oct 2026).
 * Pure functions: give it the day's rooms (parser output merged with live app
 * state) and the current time; it returns sections + a reason list per room.
 *
 * Rules agreed with Ronan, 26 Oct 2026:
 *   checkout 11:00 · default check-in 15:00 · a clean takes 45 minutes
 *   premium = Honeymoon Suite, Champagne Suite, all Deluxe
 *   order: reception "Do next" pins → guest waiting → early check-in →
 *          arrivals by start-by (ETA − 45 min; premium wins ties) → stayovers →
 *          leaving/no arrival (premium first) → empty dirty.
 *   Only rooms that can be started now are numbered; the rest sit in WAITING.
 *   Lodges (across the car park) are batched in their own group; OOS hidden.
 */

export const RULES = { checkout: '11:00', checkin: '15:00', cleanMins: 45, paidLateCheckout: '14:00' };
const ARRIVING = ['turn', 'arr'];

export const toMins = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(':').map((x) => parseInt(x, 10));
  return Number.isFinite(h) ? h * 60 + (m || 0) : null;
};
export const fromMins = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** r: { room, job, premium, sectorKey, eta, lateCheckout, guestGone, status,
 *       override: 'next'|'hold'|null, overrideAt, holdReason, guestWaiting, earlyCheckin, dnd } */
export function startBy(r, rules = RULES) {
  if (!ARRIVING.includes(r.job)) return null;
  return toMins(r.eta || rules.checkin) - rules.cleanMins;
}

export function canStart(r, nowMins, rules = RULES) {
  if (r.override === 'hold') return false;
  if (r.job === 'oos' || r.job === 'vacant' || r.job === 'in') return false;
  if (r.job === 'turn' || r.job === 'out') {
    if (!r.guestGone) return false;
    if (r.lateCheckout && nowMins < toMins(r.lateCheckout)) return false;
  }
  return true;
}

export function tier(r) {
  if (r.override === 'next') return 0;
  if (r.guestWaiting) return 1;
  if (r.earlyCheckin) return 2;
  if (ARRIVING.includes(r.job)) return 3;
  if (r.job === 'stay') return r.dnd ? 6 : 4;
  if (r.job === 'out') return 5;
  return 7;
}

export function sortKey(r, rules = RULES) {
  const t = tier(r);
  if (t === 0) return [0, r.overrideAt ? Date.parse(r.overrideAt) : 0, 0, +r.room];
  const sb = startBy(r, rules);
  return [t, sb === null ? 0 : sb, r.premium ? 0 : 1, +r.room];
}

const cmp = (a, b) => { for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i] - b[i]; return 0; };

/** Plain-English reasons shown on the card, most important first. */
export function reasons(r, nowMins, rules = RULES) {
  const out = [];
  const push = (text, tone) => out.push({ text, tone });
  if (r.override === 'next') push('Reception: do next', 'urgent');
  if (r.override === 'hold') push(`On hold${r.holdReason ? ' – ' + r.holdReason : ''}`, 'warn');
  if (r.guestWaiting) push('Guest waiting at reception', 'urgent');
  if (r.earlyCheckin) push(`Early check-in${r.eta ? ' ' + r.eta : ''} – start now`, 'urgent');
  if (ARRIVING.includes(r.job) && !r.guestWaiting && !r.earlyCheckin) {
    push(`Arriving ${r.eta || 'today'} · start by ${fromMins(startBy(r, rules))}`, 'info');
  }
  if (r.job === 'turn') push(r.guestGone ? 'Guest gone ✓' : 'Guest still in room', r.guestGone ? 'ok' : 'warn');
  if (r.job === 'arr') push('Room empty', 'ok');
  if (r.job === 'stay') push(r.dnd ? 'Do not disturb' : 'Stayover', r.dnd ? 'warn' : 'info');
  if (r.job === 'out') push(r.guestGone ? 'Guest gone · nobody arriving' : 'Guest still in · nobody arriving', 'muted');
  if (r.job === 'dirty') push('Empty, left dirty · nobody arriving', 'muted');
  if (r.lateCheckout) push(`Late checkout ${r.lateCheckout} – do not enter before`, 'warn');
  else if (r.lateBooked && !r.guestGone) push('Late checkout booked – wait until the guest leaves', 'warn');
  if ((r.job === 'turn' || r.job === 'out') && !r.guestGone && !r.lateCheckout && nowMins > toMins(rules.checkout)) {
    push(`Past ${rules.checkout} checkout – reception alerted`, 'urgent');
  }
  (r.flags || []).forEach((f) => push(f, 'muted'));
  if (conflict(r, rules)) push('Clash: late checkout leaves too little time before arrival', 'urgent');
  return out;
}

/** Late checkout + clean time runs past the arriving guest's ETA. */
export function conflict(r, rules = RULES) {
  if (r.job !== 'turn' || !r.lateCheckout) return false;
  return toMins(r.lateCheckout) + rules.cleanMins > toMins(r.eta || rules.checkin);
}

/**
 * @returns {{ next: [], then: [], waiting: [], lodges: [], done: [] }}
 */
export function plan(rooms, nowMins, rules = RULES) {
  const live = rooms.filter((r) => !['oos', 'vacant', 'in'].includes(r.job));
  const main = live.filter((r) => r.sectorKey !== 'lodges');
  const lodges = live.filter((r) => r.sectorKey === 'lodges' && r.status !== 'done');
  const done = live.filter((r) => r.status === 'done');
  const open = main.filter((r) => r.status !== 'done');
  const ready = open.filter((r) => canStart(r, nowMins, rules)).sort((a, b) => cmp(sortKey(a, rules), sortKey(b, rules)));
  const waiting = open.filter((r) => !canStart(r, nowMins, rules)).sort((a, b) => cmp(sortKey(a, rules), sortKey(b, rules)));
  const decorate = (r, i) => ({ ...r, position: i === null ? null : i + 1, why: reasons(r, nowMins, rules) });
  return {
    next: ready.slice(0, 3).map((r, i) => decorate(r, i)),
    then: ready.slice(3).map((r, i) => decorate(r, i + 3)),
    waiting: waiting.map((r) => decorate(r, null)),
    lodges: lodges.sort((a, b) => cmp(sortKey(a, rules), sortKey(b, rules))).map((r) => decorate(r, null)),
    done: done.map((r) => decorate(r, null)),
  };
}
